import { useState, useEffect, useCallback, useRef } from 'react';

export interface ResponsiveState {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  width: number;
}

/**
 * レスポンシブブレークポイントを管理するフック
 * Tailwind CSSの標準ブレークポイントに準拠
 * - Mobile: < 768px
 * - Tablet: 768px - 1023px
 * - Desktop: >= 1024px
 *
 * パフォーマンス最適化:
 * - デバウンス処理でリサイズイベントの頻度を制限
 * - requestAnimationFrameで描画フレームに同期
 */
export const useResponsive = (): ResponsiveState => {
  const [width, setWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  const frameRef = useRef<number>();
  const timeoutRef = useRef<NodeJS.Timeout>();

  const handleResize = useCallback(() => {
    // 既存のタイマーとフレームリクエストをクリア
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }

    // デバウンス処理（150ms）+ requestAnimationFrame
    timeoutRef.current = setTimeout(() => {
      frameRef.current = requestAnimationFrame(() => {
        setWidth(window.innerWidth);
      });
    }, 150);
  }, []);

  useEffect(() => {
    // 初期値を設定
    setWidth(window.innerWidth);

    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);

      // クリーンアップ
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [handleResize]);

  const isMobile = width < 768;
  const isTablet = width >= 768 && width < 1024;
  const isDesktop = width >= 1024;

  return {
    isMobile,
    isTablet,
    isDesktop,
    width,
  };
};
